/**
 * IgamingPlayerSegmentationPage — retention dashboard for iGaming player
 * activity tiers (feature #158 / OPUS DASH-158).
 *
 * The page aggregates the four surfaces exposed by the API at
 * `/api/v1/igaming-player-segmentation`:
 *
 *   1. Three tier tiles (VIP / Casual / Dormant) — each shows the player
 *      count and the per-tier lifetime value (LTV) plus supporting
 *      aggregates (deposits, bets, sessions).
 *   2. A "Players in tier" table — paginated list of the selected tier.
 *   3. A per-player lookup form — resolve one player by id and show their
 *      full activity profile.
 *   4. An activity upsert form — write lifetime metrics; the page shows a
 *      live tier preview based on the form inputs and the VIP thresholds.
 *
 * Style tokens mirror the rest of dash: zinc borders, 6-8px rounding, the
 * emerald -> sky headline gradient. Light theme only; no emojis; no
 * competitor names.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
	DORMANT_SECONDS_THRESHOLD,
	PLAYER_TIERS,
	VIP_DEPOSIT_CENTS_THRESHOLD,
	VIP_NGR_CENTS_THRESHOLD,
	activityFormSchema,
	classifyTier,
	fetchPlayer,
	fetchSegmentPlayers,
	fetchSegments,
	formatCents,
	formatCount,
	tierDescription,
	tierLabel,
	upsertPlayerActivity,
	type PlayerRow,
	type PlayerTier,
	type SegmentTileRow,
	type SegmentsResponse,
} from "./api";

// ---------------------------------------------------------------------------
// Static style tokens
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const BTN_PRIMARY =
	"inline-flex items-center justify-center rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"inline-flex items-center justify-center rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const TIER_BADGE: Record<PlayerTier, string> = {
	vip: "bg-emerald-50 text-emerald-700 border-emerald-200",
	casual: "bg-sky-50 text-sky-700 border-sky-200",
	dormant: "bg-zinc-100 text-zinc-700 border-zinc-200",
};

// ---------------------------------------------------------------------------
// Page root
// ---------------------------------------------------------------------------

export function IgamingPlayerSegmentationPage() {
	return (
		<main className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10 sm:py-14">
			<Breadcrumb />
			<Heading />
			<SegmentsStrip />
			<SegmentPlayersSection />
			<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
				<PlayerLookupCard />
				<ActivityUpsertCard />
			</div>
			<DocHint />
		</main>
	);
}

export default IgamingPlayerSegmentationPage;

// ---------------------------------------------------------------------------
// Header fragments
// ---------------------------------------------------------------------------

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<a
						href="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</a>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5">iGaming</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">Player segmentation</li>
			</ol>
		</nav>
	);
}

function Heading() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				iGaming retention
			</p>
			<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
				Player segmentation by activity tier
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Classifies every player into one of three retention tiers and exposes
				the per-tier lifetime value so CRM tooling can size cohorts, staff VIP
				hospitality, and queue dormant players for re-activation.
			</p>
		</header>
	);
}

// ---------------------------------------------------------------------------
// Segments strip (three tier tiles)
// ---------------------------------------------------------------------------

interface SegmentsState {
	loading: boolean;
	data: SegmentsResponse | null;
	error: string | null;
}

const EMPTY_SEGMENTS: SegmentsState = {
	loading: true,
	data: null,
	error: null,
};

function SegmentsStrip() {
	const [state, setState] = useState<SegmentsState>(EMPTY_SEGMENTS);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const data = await fetchSegments();
				if (cancelled) return;
				setState({ loading: false, data, error: null });
			} catch (err) {
				if (cancelled) return;
				setState({
					loading: false,
					data: null,
					error:
						err instanceof Error ? err.message : "Unable to load segments.",
				});
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	const tiles = state.data?.tiers ?? PLAYER_TIERS.map((tier) => ({
		tier,
		player_count: 0,
		total_ltv_cents: 0,
		total_deposits_cents: 0,
		total_bets_cents: 0,
		total_sessions: 0,
	}));

	return (
		<section aria-label="Tier summary" className="flex flex-col gap-3">
			<div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
				{tiles.map((tile) => (
					<TierTile key={tile.tier} tile={tile} loading={state.loading} />
				))}
			</div>
			{state.error && (
				<p className={ERROR_TEXT} role="status">
					{state.error}
				</p>
			)}
		</section>
	);
}

function TierTile(props: { tile: SegmentTileRow; loading: boolean }) {
	const { tile, loading } = props;
	return (
		<div className={`${CARD} flex flex-col gap-3 p-5`}>
			<header className="flex items-center justify-between gap-2">
				<span
					className={`inline-flex items-center rounded-[6px] border px-2 py-0.5 text-xs font-semibold uppercase tracking-[0.1em] ${TIER_BADGE[tile.tier]}`}
				>
					{tierLabel(tile.tier)}
				</span>
				<span className={HINT}>{tierDescription(tile.tier)}</span>
			</header>
			<div className="flex flex-col gap-1">
				<p className="text-xs font-medium uppercase tracking-[0.08em] text-zinc-500">
					Players
				</p>
				<p className="text-2xl font-semibold text-zinc-900">
					{loading ? "—" : formatCount(tile.player_count)}
				</p>
			</div>
			<dl className="grid grid-cols-2 gap-3 text-sm">
				<div className="flex flex-col gap-1">
					<dt className={HINT}>Lifetime value</dt>
					<dd className="font-medium text-zinc-900">
						{loading ? "—" : formatCents(tile.total_ltv_cents)}
					</dd>
				</div>
				<div className="flex flex-col gap-1">
					<dt className={HINT}>Total deposits</dt>
					<dd className="font-medium text-zinc-900">
						{loading ? "—" : formatCents(tile.total_deposits_cents)}
					</dd>
				</div>
				<div className="flex flex-col gap-1">
					<dt className={HINT}>Total bets</dt>
					<dd className="font-medium text-zinc-900">
						{loading ? "—" : formatCents(tile.total_bets_cents)}
					</dd>
				</div>
				<div className="flex flex-col gap-1">
					<dt className={HINT}>Sessions</dt>
					<dd className="font-medium text-zinc-900">
						{loading ? "—" : formatCount(tile.total_sessions)}
					</dd>
				</div>
			</dl>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Segment players table
// ---------------------------------------------------------------------------

interface PlayersState {
	loading: boolean;
	rows: PlayerRow[];
	total: number;
	error: string | null;
}

const PAGE_SIZE = 10;

function SegmentPlayersSection() {
	const [tier, setTier] = useState<PlayerTier>("vip");
	const [offset, setOffset] = useState(0);
	const [state, setState] = useState<PlayersState>({
		loading: true,
		rows: [],
		total: 0,
		error: null,
	});

	useEffect(() => {
		let cancelled = false;
		(async () => {
			setState((prev) => ({ ...prev, loading: true }));
			try {
				const res = await fetchSegmentPlayers(tier, {
					limit: PAGE_SIZE,
					offset,
				});
				if (cancelled) return;
				setState({
					loading: false,
					rows: res.players,
					total: res.total,
					error: null,
				});
			} catch (err) {
				if (cancelled) return;
				setState({
					loading: false,
					rows: [],
					total: 0,
					error:
						err instanceof Error ? err.message : "Unable to load players.",
				});
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [tier, offset]);

	const canPrev = offset > 0;
	const canNext = offset + PAGE_SIZE < state.total;

	return (
		<section
			aria-label="Players in tier"
			className={`${CARD} flex flex-col gap-4 p-6`}
		>
			<header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
				<div className="flex flex-col gap-1">
					<h2 className="text-lg font-semibold text-zinc-900">
						Players in tier
					</h2>
					<p className={HINT}>
						Sorted by lifetime NGR, descending. Page size {PAGE_SIZE}.
					</p>
				</div>
				<div
					role="tablist"
					aria-label="Tier filter"
					className="inline-flex rounded-[6px] border border-zinc-200 bg-zinc-50 p-0.5"
				>
					{PLAYER_TIERS.map((t) => {
						const active = t === tier;
						return (
							<button
								key={t}
								type="button"
								role="tab"
								aria-selected={active}
								className={`rounded-[4px] px-3 py-1 text-xs font-medium ${
									active
										? "bg-white text-zinc-900 shadow-sm"
										: "text-zinc-600 hover:text-zinc-900"
								}`}
								onClick={() => {
									setTier(t);
									setOffset(0);
								}}
							>
								{tierLabel(t)}
							</button>
						);
					})}
				</div>
			</header>
			<div className="overflow-x-auto rounded-[6px] border border-zinc-200">
				<table className="min-w-full divide-y divide-zinc-200 text-sm">
					<thead className="bg-zinc-50 text-xs uppercase tracking-[0.08em] text-zinc-500">
						<tr>
							<th scope="col" className="px-3 py-2 text-left">
								Player
							</th>
							<th scope="col" className="px-3 py-2 text-right">
								Lifetime NGR
							</th>
							<th scope="col" className="px-3 py-2 text-right">
								Deposits
							</th>
							<th scope="col" className="px-3 py-2 text-right">
								Sessions
							</th>
							<th scope="col" className="px-3 py-2 text-left">
								Last activity
							</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-zinc-100">
						{state.loading ? (
							<tr>
								<td colSpan={5} className="px-3 py-3 text-zinc-500">
									Loading.
								</td>
							</tr>
						) : state.rows.length === 0 ? (
							<tr>
								<td colSpan={5} className="px-3 py-3 text-zinc-500">
									{state.error ?? "No players in this tier."}
								</td>
							</tr>
						) : (
							state.rows.map((row) => (
								<tr key={row.id}>
									<td className="px-3 py-2 font-mono text-xs text-zinc-800">
										{row.player_id}
									</td>
									<td className="px-3 py-2 text-right font-medium text-zinc-900">
										{formatCents(row.lifetime_ngr_cents)}
									</td>
									<td className="px-3 py-2 text-right text-zinc-700">
										{formatCents(row.lifetime_deposits_cents)}
									</td>
									<td className="px-3 py-2 text-right text-zinc-700">
										{formatCount(row.session_count)}
									</td>
									<td className="px-3 py-2 text-zinc-600">
										{formatTimestamp(row.last_activity_ts)}
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>
			<footer className="flex items-center justify-between gap-2">
				<p className={HINT}>
					Showing {state.rows.length} of {formatCount(state.total)}{" "}
					{tierLabel(tier).toLowerCase()} players.
				</p>
				<div className="flex items-center gap-2">
					<button
						type="button"
						className={BTN_SECONDARY}
						onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
						disabled={!canPrev}
					>
						Previous
					</button>
					<button
						type="button"
						className={BTN_SECONDARY}
						onClick={() => setOffset(offset + PAGE_SIZE)}
						disabled={!canNext}
					>
						Next
					</button>
				</div>
			</footer>
		</section>
	);
}

function formatTimestamp(seconds: number): string {
	if (!Number.isFinite(seconds) || seconds <= 0) return "—";
	const ms = Math.floor(seconds) * 1000;
	try {
		const d = new Date(ms);
		const y = d.getUTCFullYear().toString().padStart(4, "0");
		const m = (d.getUTCMonth() + 1).toString().padStart(2, "0");
		const day = d.getUTCDate().toString().padStart(2, "0");
		return `${y}-${m}-${day}`;
	} catch {
		return "—";
	}
}

// ---------------------------------------------------------------------------
// Player lookup card
// ---------------------------------------------------------------------------

interface LookupState {
	query: string;
	loading: boolean;
	player: PlayerRow | null;
	error: string | null;
	notFound: boolean;
}

function PlayerLookupCard() {
	const [state, setState] = useState<LookupState>({
		query: "",
		loading: false,
		player: null,
		error: null,
		notFound: false,
	});

	const onSubmit = useCallback(
		async (event: React.FormEvent<HTMLFormElement>) => {
			event.preventDefault();
			const q = state.query.trim();
			if (!q) {
				setState((prev) => ({
					...prev,
					error: "Enter a player id to look up.",
					notFound: false,
					player: null,
				}));
				return;
			}
			setState((prev) => ({
				...prev,
				loading: true,
				error: null,
				notFound: false,
				player: null,
			}));
			try {
				const row = await fetchPlayer(q);
				if (row === null) {
					setState((prev) => ({
						...prev,
						loading: false,
						notFound: true,
					}));
					return;
				}
				setState((prev) => ({
					...prev,
					loading: false,
					player: row,
				}));
			} catch (err) {
				setState((prev) => ({
					...prev,
					loading: false,
					error:
						err instanceof Error ? err.message : "Unable to look up player.",
				}));
			}
		},
		[state.query],
	);

	return (
		<section
			aria-label="Player lookup"
			className={`${CARD} flex flex-col gap-4 p-6`}
		>
			<header className="flex flex-col gap-1">
				<h2 className="text-lg font-semibold text-zinc-900">Player lookup</h2>
				<p className={HINT}>
					Fetch one player by id and review their full activity profile.
				</p>
			</header>
			<form
				onSubmit={onSubmit}
				className="flex flex-col gap-3"
				noValidate
				aria-label="Player lookup form"
			>
				<div className="flex flex-col gap-1.5">
					<label htmlFor="pls-lookup-id" className={LABEL}>
						Player ID
					</label>
					<input
						id="pls-lookup-id"
						name="playerId"
						type="text"
						placeholder="pla_abc123"
						className={INPUT}
						value={state.query}
						onChange={(e) =>
							setState((prev) => ({
								...prev,
								query: e.currentTarget.value,
								error: null,
								notFound: false,
							}))
						}
						disabled={state.loading}
					/>
				</div>
				<div className="flex justify-end">
					<button type="submit" className={BTN_PRIMARY} disabled={state.loading}>
						{state.loading ? "Looking up." : "Look up"}
					</button>
				</div>
				{state.error && (
					<p className={ERROR_TEXT} role="alert">
						{state.error}
					</p>
				)}
				{state.notFound && (
					<p className={HINT} role="status">
						No player found for that id in the current tenant.
					</p>
				)}
			</form>
			{state.player && <PlayerSummary player={state.player} />}
		</section>
	);
}

function PlayerSummary(props: { player: PlayerRow }) {
	const { player } = props;
	return (
		<div className="rounded-[6px] border border-zinc-200 bg-zinc-50 p-4">
			<header className="flex items-center justify-between gap-2">
				<p className="font-mono text-sm text-zinc-800">{player.player_id}</p>
				<span
					className={`inline-flex items-center rounded-[6px] border px-2 py-0.5 text-xs font-semibold uppercase tracking-[0.1em] ${TIER_BADGE[player.tier]}`}
				>
					{tierLabel(player.tier)}
				</span>
			</header>
			<dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
				<div className="flex flex-col gap-1">
					<dt className={HINT}>Lifetime NGR</dt>
					<dd className="font-medium text-zinc-900">
						{formatCents(player.lifetime_ngr_cents)}
					</dd>
				</div>
				<div className="flex flex-col gap-1">
					<dt className={HINT}>Lifetime deposits</dt>
					<dd className="font-medium text-zinc-900">
						{formatCents(player.lifetime_deposits_cents)}
					</dd>
				</div>
				<div className="flex flex-col gap-1">
					<dt className={HINT}>Lifetime bets</dt>
					<dd className="font-medium text-zinc-900">
						{formatCents(player.lifetime_bets_cents)}
					</dd>
				</div>
				<div className="flex flex-col gap-1">
					<dt className={HINT}>Sessions</dt>
					<dd className="font-medium text-zinc-900">
						{formatCount(player.session_count)}
					</dd>
				</div>
				<div className="flex flex-col gap-1">
					<dt className={HINT}>Last activity</dt>
					<dd className="font-medium text-zinc-900">
						{formatTimestamp(player.last_activity_ts)}
					</dd>
				</div>
				<div className="flex flex-col gap-1">
					<dt className={HINT}>Updated</dt>
					<dd className="font-medium text-zinc-900">
						{formatTimestamp(player.updated_at)}
					</dd>
				</div>
			</dl>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Activity upsert card
// ---------------------------------------------------------------------------

interface UpsertState {
	playerId: string;
	lifetimeNgrCents: string;
	lifetimeDepositsCents: string;
	lifetimeBetsCents: string;
	sessionCount: string;
	lastActivityTs: string;
	errors: Record<string, string>;
	submitting: boolean;
	success: PlayerRow | null;
	serverError: string | null;
}

const EMPTY_UPSERT: UpsertState = {
	playerId: "",
	lifetimeNgrCents: "",
	lifetimeDepositsCents: "",
	lifetimeBetsCents: "",
	sessionCount: "",
	lastActivityTs: "",
	errors: {},
	submitting: false,
	success: null,
	serverError: null,
};

function ActivityUpsertCard() {
	const [state, setState] = useState<UpsertState>(EMPTY_UPSERT);

	const parseResult = useMemo(
		() =>
			activityFormSchema.safeParse({
				playerId: state.playerId,
				lifetimeNgrCents: state.lifetimeNgrCents,
				lifetimeDepositsCents: state.lifetimeDepositsCents,
				lifetimeBetsCents: state.lifetimeBetsCents,
				sessionCount: state.sessionCount,
				lastActivityTs: state.lastActivityTs,
			}),
		[
			state.playerId,
			state.lifetimeNgrCents,
			state.lifetimeDepositsCents,
			state.lifetimeBetsCents,
			state.sessionCount,
			state.lastActivityTs,
		],
	);

	const previewTier: PlayerTier | null = useMemo(() => {
		if (!parseResult.success) return null;
		return classifyTier({
			lifetime_ngr_cents: parseResult.data.lifetimeNgrCents,
			lifetime_deposits_cents: parseResult.data.lifetimeDepositsCents,
			last_activity_ts: parseResult.data.lastActivityTs,
			now_seconds: Math.floor(Date.now() / 1000),
		});
	}, [parseResult]);

	const onSubmit = useCallback(
		async (event: React.FormEvent<HTMLFormElement>) => {
			event.preventDefault();
			const parsed = activityFormSchema.safeParse({
				playerId: state.playerId,
				lifetimeNgrCents: state.lifetimeNgrCents,
				lifetimeDepositsCents: state.lifetimeDepositsCents,
				lifetimeBetsCents: state.lifetimeBetsCents,
				sessionCount: state.sessionCount,
				lastActivityTs: state.lastActivityTs,
			});
			if (!parsed.success) {
				setState((prev) => ({
					...prev,
					errors: parsed.errors,
					success: null,
					serverError: null,
				}));
				return;
			}
			setState((prev) => ({
				...prev,
				errors: {},
				submitting: true,
				success: null,
				serverError: null,
			}));
			try {
				const row = await upsertPlayerActivity(parsed.data);
				setState({
					...EMPTY_UPSERT,
					success: row,
				});
			} catch (err) {
				setState((prev) => ({
					...prev,
					submitting: false,
					serverError:
						err instanceof Error ? err.message : "Unable to save activity.",
				}));
			}
		},
		[state],
	);

	return (
		<section
			aria-label="Upsert player activity"
			className={`${CARD} flex flex-col gap-4 p-6`}
		>
			<header className="flex flex-col gap-1">
				<h2 className="text-lg font-semibold text-zinc-900">
					Record player activity
				</h2>
				<p className={HINT}>
					Upsert lifetime metrics for one player. The preview below shows the
					tier the record will land in.
				</p>
			</header>
			<form
				onSubmit={onSubmit}
				className="flex flex-col gap-3"
				noValidate
				aria-label="Upsert player activity form"
			>
				<div className="flex flex-col gap-1.5">
					<label htmlFor="pls-upsert-id" className={LABEL}>
						Player ID
					</label>
					<input
						id="pls-upsert-id"
						name="playerId"
						type="text"
						placeholder="pla_abc123"
						className={INPUT}
						value={state.playerId}
						onChange={(e) =>
							setState((prev) => ({
								...prev,
								playerId: e.currentTarget.value,
								errors: { ...prev.errors, playerId: "" },
							}))
						}
						aria-invalid={Boolean(state.errors.playerId)}
						aria-describedby={
							state.errors.playerId ? "pls-upsert-id-error" : undefined
						}
						disabled={state.submitting}
					/>
					{state.errors.playerId && (
						<p id="pls-upsert-id-error" className={ERROR_TEXT}>
							{state.errors.playerId}
						</p>
					)}
				</div>
				<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
					<NumberField
						id="pls-upsert-ngr"
						label="Lifetime NGR (cents)"
						name="lifetimeNgrCents"
						value={state.lifetimeNgrCents}
						error={state.errors.lifetimeNgrCents}
						disabled={state.submitting}
						onChange={(v) =>
							setState((prev) => ({
								...prev,
								lifetimeNgrCents: v,
								errors: { ...prev.errors, lifetimeNgrCents: "" },
							}))
						}
					/>
					<NumberField
						id="pls-upsert-deposits"
						label="Lifetime deposits (cents)"
						name="lifetimeDepositsCents"
						value={state.lifetimeDepositsCents}
						error={state.errors.lifetimeDepositsCents}
						disabled={state.submitting}
						onChange={(v) =>
							setState((prev) => ({
								...prev,
								lifetimeDepositsCents: v,
								errors: { ...prev.errors, lifetimeDepositsCents: "" },
							}))
						}
					/>
					<NumberField
						id="pls-upsert-bets"
						label="Lifetime bets (cents)"
						name="lifetimeBetsCents"
						value={state.lifetimeBetsCents}
						error={state.errors.lifetimeBetsCents}
						disabled={state.submitting}
						onChange={(v) =>
							setState((prev) => ({
								...prev,
								lifetimeBetsCents: v,
								errors: { ...prev.errors, lifetimeBetsCents: "" },
							}))
						}
					/>
					<NumberField
						id="pls-upsert-sessions"
						label="Session count"
						name="sessionCount"
						value={state.sessionCount}
						error={state.errors.sessionCount}
						disabled={state.submitting}
						onChange={(v) =>
							setState((prev) => ({
								...prev,
								sessionCount: v,
								errors: { ...prev.errors, sessionCount: "" },
							}))
						}
					/>
				</div>
				<NumberField
					id="pls-upsert-last-ts"
					label="Last activity (Unix seconds, UTC)"
					name="lastActivityTs"
					value={state.lastActivityTs}
					error={state.errors.lastActivityTs}
					disabled={state.submitting}
					onChange={(v) =>
						setState((prev) => ({
							...prev,
							lastActivityTs: v,
							errors: { ...prev.errors, lastActivityTs: "" },
						}))
					}
				/>

				<TierPreview tier={previewTier} />

				<div className="flex justify-end">
					<button type="submit" className={BTN_PRIMARY} disabled={state.submitting}>
						{state.submitting ? "Saving." : "Save activity"}
					</button>
				</div>

				{state.success && (
					<div
						className="rounded-[6px] border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800"
						role="status"
					>
						Saved {state.success.player_id} as {tierLabel(state.success.tier)}.
					</div>
				)}
				{state.serverError && (
					<p className={ERROR_TEXT} role="alert">
						{state.serverError}
					</p>
				)}
			</form>
		</section>
	);
}

function NumberField(props: {
	id: string;
	label: string;
	name: string;
	value: string;
	error?: string;
	disabled: boolean;
	onChange: (v: string) => void;
}) {
	return (
		<div className="flex flex-col gap-1.5">
			<label htmlFor={props.id} className={LABEL}>
				{props.label}
			</label>
			<input
				id={props.id}
				name={props.name}
				type="number"
				min={0}
				step={1}
				inputMode="numeric"
				className={INPUT}
				value={props.value}
				onChange={(e) => props.onChange(e.currentTarget.value)}
				aria-invalid={Boolean(props.error)}
				aria-describedby={props.error ? `${props.id}-error` : undefined}
				disabled={props.disabled}
			/>
			{props.error && (
				<p id={`${props.id}-error`} className={ERROR_TEXT}>
					{props.error}
				</p>
			)}
		</div>
	);
}

function TierPreview(props: { tier: PlayerTier | null }) {
	return (
		<div className="rounded-[6px] border border-zinc-200 bg-zinc-50 p-3">
			<p className={HINT}>Predicted tier</p>
			{props.tier ? (
				<div className="mt-1 flex items-center gap-2">
					<span
						className={`inline-flex items-center rounded-[6px] border px-2 py-0.5 text-xs font-semibold uppercase tracking-[0.1em] ${TIER_BADGE[props.tier]}`}
					>
						{tierLabel(props.tier)}
					</span>
					<span className={HINT}>{tierDescription(props.tier)}</span>
				</div>
			) : (
				<p className="mt-1 text-sm text-zinc-500">
					Fill in every field to see the tier preview.
				</p>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Footer hint
// ---------------------------------------------------------------------------

function DocHint() {
	return (
		<aside className="rounded-[8px] border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-600">
			<p className="font-medium text-zinc-800">Tier rules</p>
			<ul className="mt-2 flex list-inside list-disc flex-col gap-1">
				<li>
					VIP: lifetime NGR at least {formatCents(VIP_NGR_CENTS_THRESHOLD)} OR
					lifetime deposits at least {formatCents(VIP_DEPOSIT_CENTS_THRESHOLD)}.
				</li>
				<li>
					Dormant: no activity for{" "}
					{Math.floor(DORMANT_SECONDS_THRESHOLD / (24 * 60 * 60))} days or more,
					and not VIP.
				</li>
				<li>
					Casual: everyone else (recent activity, below the VIP thresholds).
				</li>
				<li>
					VIP takes precedence over dormant: high-LTV players idle for months
					remain VIP so retention workflows treat them as hospitality, not
					churn.
				</li>
			</ul>
		</aside>
	);
}
